(() => {
  const SELECTOR = "[data-callback-request]";
  const MODAL_SELECTOR = "[data-callback-modal]";
  let activeModal = null;
  let previousFocus = null;

  function focusableElements(modal) {
    return Array.from(
      modal.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (element) =>
        !element.hidden &&
        element.getAttribute("aria-hidden") !== "true" &&
        element.tabIndex >= 0
    );
  }

  function openModal(modal) {
    if (!modal) return;
    previousFocus = document.activeElement;
    activeModal = modal;
    modal.hidden = false;
    document.documentElement.classList.add("callback-request-modal-open");
    modal.querySelector(".callback-request-modal__close")?.focus();
  }

  function closeModal(modal = activeModal) {
    if (!modal) return;
    modal.hidden = true;
    document.documentElement.classList.remove("callback-request-modal-open");
    activeModal = null;
    if (previousFocus instanceof HTMLElement) previousFocus.focus();
    previousFocus = null;
  }

  function initModal(modal) {
    if (modal.dataset.callbackModalInitialized === "true") return;
    modal.dataset.callbackModalInitialized = "true";
    modal.querySelectorAll("[data-callback-modal-close]").forEach((button) => {
      button.addEventListener("click", () => closeModal(modal));
    });
  }

  function createSubmissionId() {
    const date = new Date().toISOString().slice(0, 10).replaceAll("-", "");
    const random = globalThis.crypto?.randomUUID
      ? globalThis.crypto.randomUUID().replaceAll("-", "").slice(0, 8)
      : Math.random().toString(36).slice(2, 10);

    return `LCB-${date}-${random.toUpperCase()}`;
  }

  function maskPhone(phone) {
    const digits = phone.replace(/\D/g, "");
    if (digits.length < 4) return "the number provided";
    return `the number ending in ${digits.slice(-4)}`;
  }

  function setBusy(form, busy) {
    const button = form.querySelector("[data-callback-submit]");
    if (!button) return;
    button.disabled = busy;
    button.setAttribute("aria-busy", String(busy));
  }

  function showStatus(root, message) {
    const status = root.querySelector("[data-callback-status]");
    if (!status) return;
    status.textContent = message;
    status.hidden = false;
    status.focus();
  }

  function clearStatus(root) {
    const status = root.querySelector("[data-callback-status]");
    if (!status) return;
    status.textContent = "";
    status.hidden = true;
  }

  function validatePhone(root, input) {
    const error = root.querySelector("[data-phone-error]");
    const digits = input.value.replace(/\D/g, "");
    const valid = digits.length >= 7 && digits.length <= 15;

    input.setCustomValidity(valid ? "" : "Enter a valid callback phone number.");
    if (error) error.hidden = valid;
    return valid;
  }

  function selectedText(select) {
    return select.options[select.selectedIndex]?.text || "";
  }

  function showSuccess(root, payload) {
    const formPanel = root.querySelector("[data-callback-form-panel]");
    const success = root.querySelector("[data-callback-success]");
    const phone = root.querySelector("[data-success-phone]");
    const reference = root.querySelector("[data-success-reference]");

    if (formPanel) formPanel.hidden = true;
    if (phone) phone.textContent = maskPhone(payload.phone);
    if (reference) reference.textContent = payload.submission_id;
    if (success) {
      success.hidden = false;
      success.focus();
    }
  }

  function init(root) {
    if (root.dataset.callbackInitialized === "true") return;
    root.dataset.callbackInitialized = "true";

    const form = root.querySelector("[data-callback-form]");
    const details = root.querySelector("[data-callback-details]");
    const counter = root.querySelector("[data-details-counter]");
    const phone = root.querySelector("[data-callback-phone]");
    const reset = root.querySelector("[data-callback-reset]");

    if (!form) return;

    const updateCounter = () => {
      if (counter && details) counter.textContent = `${details.value.length}/1000`;
    };

    details?.addEventListener("input", updateCounter);
    updateCounter();

    phone?.addEventListener("input", () => {
      phone.setCustomValidity("");
      const error = root.querySelector("[data-phone-error]");
      if (error) error.hidden = true;
    });

    reset?.addEventListener("click", () => {
      const panel = root.querySelector("[data-callback-form-panel]");
      const success = root.querySelector("[data-callback-success]");
      form.reset();
      updateCounter();
      if (success) success.hidden = true;
      if (panel) panel.hidden = false;
      form.querySelector("input")?.focus();
    });

    form.addEventListener("submit", async (event) => {
      event.preventDefault();
      clearStatus(root);

      if (!form.checkValidity()) {
        form.reportValidity();
        form.querySelector(":invalid")?.focus();
        return;
      }

      if (phone && !validatePhone(root, phone)) {
        phone.reportValidity();
        phone.focus();
        return;
      }

      const honeypot = form.querySelector('[name="website"]');
      if (honeypot?.value) {
        showSuccess(root, {
          phone: form.elements.phone.value,
          submission_id: createSubmissionId(),
        });
        return;
      }

      const endpoint = root.dataset.endpoint;
      const enabled = root.dataset.enabled === "true";
      if (!enabled || !endpoint) {
        showStatus(root, root.dataset.configurationError);
        return;
      }

      const reason = form.elements.reason;
      const preferredTime = form.elements.preferred_time;
      const payload = {
        submission_id: createSubmissionId(),
        submitted_at: new Date().toISOString(),
        full_name: form.elements.full_name.value.trim(),
        email: form.elements.email.value.trim().toLowerCase(),
        phone: form.elements.phone.value.trim(),
        reason: reason.value,
        reason_label: selectedText(reason),
        details: form.elements.details.value.trim(),
        preferred_time: preferredTime.value,
        preferred_time_label: selectedText(preferredTime),
        callback_consent: form.elements.callback_consent.checked,
        source: root.dataset.source,
        page_path: window.location.pathname,
        shop_domain: root.dataset.shop,
      };

      setBusy(form, true);
      const controller = new AbortController();
      const timeout = window.setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(endpoint, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
          signal: controller.signal,
        });

        if (!response.ok) throw new Error(`Callback request failed: ${response.status}`);

        showSuccess(root, payload);
        form.reset();
        updateCounter();
        document.dispatchEvent(
          new CustomEvent("lunavia:callback-submitted", {
            detail: {
              submissionId: payload.submission_id,
              reason: payload.reason,
            },
          })
        );
      } catch (error) {
        console.error("Unable to submit callback request", error);
        showStatus(root, root.dataset.submissionError);
      } finally {
        window.clearTimeout(timeout);
        setBusy(form, false);
      }
    });
  }

  function initAll(container = document) {
    container.querySelectorAll(SELECTOR).forEach(init);
    container.querySelectorAll(MODAL_SELECTOR).forEach(initModal);
  }

  document.addEventListener("lunavia:open-callback", () => {
    openModal(document.querySelector(MODAL_SELECTOR));
  });

  document.addEventListener("keydown", (event) => {
    if (!activeModal) return;

    if (event.key === "Escape") {
      event.preventDefault();
      closeModal();
      return;
    }

    if (event.key !== "Tab") return;
    const focusable = focusableElements(activeModal);
    if (!focusable.length) {
      event.preventDefault();
      activeModal.querySelector("[data-callback-modal-dialog]")?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAll());
  } else {
    initAll();
  }

  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
